import { faBan, faDownload, faEdit, faEye, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import Checkbox from '@mui/material/Checkbox';
import MuiStack from '@mui/material/Stack';
import MuiTableCell from '@mui/material/TableCell';
import MuiTableRow from '@mui/material/TableRow';
import React, { useCallback } from 'react';

import IconButton from 'components/button/IconButton';
import { useLocale } from 'hooks';
import { dateTimeToString, dateToString } from 'lib/utils';

function TableRow({ row, headCells, isItemSelected, labelId, dispatch, onSelect, onClickView, onClickEdit, onClickDelete, onClickUnsubscribe, showSelect }) {
  const locale = useLocale();
  const onSelectHandler = useCallback((event) => onSelect(event, row.id), [onSelect, row.id]);
  const onClickViewHandler = useCallback((event) => {
    event.stopPropagation();
    onClickView && onClickView(row);
  }, [onClickView, row]);
  const onClickEditHandler = useCallback((event) => {
    event.stopPropagation();
    onClickEdit && onClickEdit(row);
  }, [onClickEdit, row]);
  const onClickDeleteHandler = useCallback((event) => {
    event.stopPropagation();
    onClickDelete && onClickDelete(row);
  }, [onClickDelete, row]);

  const onClickUnsubscribeHandler = useCallback((event) => {
    event.stopPropagation();
    onClickUnsubscribe && onClickUnsubscribe(row);
  }, [onClickUnsubscribe, row]);

  const renderActionButton = useCallback((action, index) => {
    switch (action) {
      case 'view':
        return (
          <IconButton
            key={`action-${index}`}
            icon={faEye}
            tooltip='View'
            size='small'
            color='primary'
            onClick={onClickViewHandler}
          />
        );
      case 'edit':
        return onClickEdit && (
          <IconButton
            key={`action-${index}`}
            icon={faEdit}
            tooltip='Edit'
            size='small'
            color='primary'
            onClick={onClickEditHandler}
          />
        );
      case 'delete':
        return onClickDelete && (
          <IconButton
            key={`action-${index}`}
            icon={faTrashAlt}
            tooltip='Delete'
            size='small'
            color='error'
            onClick={onClickDeleteHandler}
          />
        );
      case 'download':
        return (
          <IconButton
            key={`action-${index}`}
            icon={faDownload}
            tooltip='Download'
            size='small'
            color='primary'
          />
        );
      case 'unsubscribe':
        return (
          row.status === 'Enabled' &&
          <IconButton
            key={`action-${index}`}
            icon={faBan}
            tooltip='Unsubscribe'
            size='small'
            color='error'
            onClick={onClickUnsubscribeHandler}
          />
        );
      default:
        return null;
    }
  }, [onClickViewHandler, onClickEditHandler, onClickDeleteHandler, onClickUnsubscribeHandler, onClickEdit, onClickDelete]);

  const renderTableCell = useCallback((cell, index) => {
    if (cell.render) {
      return (
        <MuiTableCell key={`table-cell-${index}`}>
          {cell.render({ row, locale, dispatch })}
        </MuiTableCell>
      );
    }

    switch (cell.type) {
      case 'actions':
        return (
          <MuiTableCell key={`table-cell-${index}`} className='accessoriesCell'>
            <MuiStack direction='row' spacing={1}>
              {cell.actions.map(renderActionButton)}
            </MuiStack>
          </MuiTableCell>
        );
      case 'datetime':
        return (
          <MuiTableCell key={`table-cell-${index}`}>
            {dateTimeToString(row[cell.id])}
          </MuiTableCell>
        );
      case 'date':
        return (
          <MuiTableCell key={`table-cell-${index}`}>
            {dateToString(row[cell.id])}
          </MuiTableCell>
        );
      default:
        if (cell.id === 'name') {
          return (
            <MuiTableCell
              key={`enhanced-table-cell-${index}`}
              id={labelId}
              scope="row"
            >
              {row.nameEng}
              {!!(row.nameEng && row.nameChi) && <br />}
              {row.nameChi}
            </MuiTableCell>
          );
        } else {
          return (
            <MuiTableCell
              key={`table-cell-${index}`}
            >
              {row[cell.id]}
            </MuiTableCell>
          );
        }
    }
  }, [row, labelId, renderActionButton, dispatch, locale]);

  return (
    <MuiTableRow
      hover
      onClick={onClickViewHandler}
      role="checkbox"
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={row.id}
      selected={isItemSelected}
      sx={{
        cursor: 'pointer',
        '& > .accessoriesCell > *': {
          opacity: 0.2,
        },
        '&:hover > .accessoriesCell > *': {
          opacity: 1,
        }
      }}
    >
      {
        showSelect && (
          <MuiTableCell padding="checkbox">
            <Checkbox
              color="primary"
              checked={isItemSelected}
              inputProps={{
                'aria-labelledby': labelId,
              }}
              onClick={onSelectHandler}
            />
          </MuiTableCell>
        )
      }
      {headCells.map(renderTableCell)}
    </MuiTableRow>
  );
}

export default TableRow;
